import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { SearchBarErrorContainer, SearchBarErrorDescription, SearchBarErrorLabel } from './SearchAndSelectVehicle.style';
import { useSelector } from 'react-redux';
import { changeVehicleSelectors } from '../../store';

export enum ErrorType {
    NO_RESULTS_FOUND = 'NO_RESULTS_FOUND',
    NO_AGENT_RESULTS_FOUND = 'NO_AGENT_RESULTS_FOUND',
    AGENT_SEARCH_TIMEOUT = 'AGENT_SEARCH_TIMEOUT',
    AGENT_SEARCH_TIMEOUT_REPEATED = 'AGENT_SEARCH_TIMEOUT_REPEATED',
    CURRENT_VEHICLE = 'CURRENT_VEHICLE'
}

const TIMEOUT_TEST_ID = {
    [ErrorType.AGENT_SEARCH_TIMEOUT]: 'search-bar-error-timeout',
    [ErrorType.AGENT_SEARCH_TIMEOUT_REPEATED]: 'search-bar-error-timeout-repeated'
} as const;

const SearchBarError = ({ errorType, onRetry }: { errorType: ErrorType; onRetry?: () => void }) => {
    const isVehicleSearchRequested = useSelector(changeVehicleSelectors.getIsVehicleSearchRequested);

    if (!isVehicleSearchRequested) {
        return null;
    }

    const errorText = {
        [ErrorType.NO_RESULTS_FOUND]: {
            title: 'No results found',
            description:
                'Make sure you have specified the appropriate filters and correct VIN, Stock #, YMMT, or Inventory Type. You may modify the search query or Build the Vehicle above.'
        },
        [ErrorType.NO_AGENT_RESULTS_FOUND]: {
            title: 'No results found from AI-assisted search',
            description:
                "Check to ensure the prompt is accurate and try again. Try adjusting your search criteria by removing filters (click the 'X' in the search bar) or building the vehicle manually."
        },
        [ErrorType.AGENT_SEARCH_TIMEOUT]: {
            title: 'Search is taking longer than expected',
            description: "We couldn't finish your search."
        },
        [ErrorType.AGENT_SEARCH_TIMEOUT_REPEATED]: {
            title: "We're having trouble searching right now",
            description: 'Try again in a few minutes, or use the filters to browse vehicles.'
        },
        [ErrorType.CURRENT_VEHICLE]: {
            title: 'This is the current vehicle',
            description: 'Make sure you have entered another VIN or Stock #, not from your current vehicle'
        }
    };

    const timeoutTestId = TIMEOUT_TEST_ID[errorType as keyof typeof TIMEOUT_TEST_ID];
    const retryButton = timeoutTestId && onRetry && (
        <Button buttonStyle="tertiary" size="small" data-testid="search-bar-error-retry" onClick={onRetry}>
            Try Again
        </Button>
    );

    if (errorType === ErrorType.AGENT_SEARCH_TIMEOUT_REPEATED) {
        return (
            <Alert data-testid={timeoutTestId} type="error" sx={{ margin: '16px 0 8px 0', '& .MuiAlert-message': { flex: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <Box>
                        <Typography variant="strong-sm" tag="div">
                            {errorText[errorType].title}
                        </Typography>
                        <Typography variant="body-sm" tag="div">
                            {errorText[errorType].description}
                        </Typography>
                    </Box>
                    {retryButton}
                </Box>
            </Alert>
        );
    }

    return (
        <SearchBarErrorContainer data-testid={timeoutTestId}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                <Box>
                    <SearchBarErrorLabel>{errorText[errorType].title}</SearchBarErrorLabel>
                    <SearchBarErrorDescription>{errorText[errorType].description}</SearchBarErrorDescription>
                </Box>
                {retryButton}
            </Box>
        </SearchBarErrorContainer>
    );
};

export default SearchBarError;
